/**
 * Picked ticket photos and their uploads, for one create form or one reply box. Each photo uploads
 * as soon as it is picked; the form sends the ids of those that landed (`readyIds`).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { uploadTicketAttachment } from '@api/services/tickets.api'
import { prepareTicketPhoto, remainingPhotoSlots, ticketPhotoError } from './ticketPhotos'

export interface PickedTicketPhoto {
  key: string
  previewUrl: string | null
  status: 'uploading' | 'ready' | 'failed'
  id?: string
  error?: string
}

let nextKey = 0

export function useTicketPhotos() {
  const [photos, setPhotos] = useState<PickedTicketPhoto[]>([])
  const objectUrls = useRef(new Map<string, string>())

  const release = useCallback((key: string) => {
    const url = objectUrls.current.get(key)
    if (url) URL.revokeObjectURL(url)
    objectUrls.current.delete(key)
  }, [])

  useEffect(() => {
    const urls = objectUrls.current
    return () => urls.forEach((url) => URL.revokeObjectURL(url))
  }, [])

  const add = useCallback(
    (files: File[]) => {
      const room = remainingPhotoSlots(photos.filter((p) => p.status !== 'failed').length)
      const taken = files.slice(0, room)
      for (const file of taken) {
        const key = `p${nextKey++}`
        let previewUrl: string | null = null
        try {
          previewUrl = URL.createObjectURL(file)
          objectUrls.current.set(key, previewUrl)
        } catch {
          previewUrl = null
        }
        setPhotos((prev) => [...prev, { key, previewUrl, status: 'uploading' }])
        void (async () => {
          try {
            const { blob, name } = await prepareTicketPhoto(file)
            const uploaded = await uploadTicketAttachment(blob, name)
            setPhotos((prev) =>
              prev.map((p) =>
                p.key === key ? { ...p, status: 'ready', id: uploaded.id, previewUrl: uploaded.thumbnailUrl } : p
              )
            )
            release(key)
          } catch (err) {
            setPhotos((prev) =>
              prev.map((p) =>
                p.key === key ? { ...p, status: 'failed', error: ticketPhotoError(err, 'That photo could not be added.') } : p
              )
            )
          }
        })()
      }
      return files.length - taken.length
    },
    [photos, release]
  )

  const remove = useCallback((key: string) => {
    release(key)
    setPhotos((prev) => prev.filter((p) => p.key !== key))
  }, [release])

  const clear = useCallback(() => {
    objectUrls.current.forEach((url) => URL.revokeObjectURL(url))
    objectUrls.current.clear()
    setPhotos([])
  }, [])

  return {
    photos,
    add,
    remove,
    clear,
    readyIds: photos.filter((p) => p.status === 'ready' && p.id).map((p) => p.id as string),
    uploading: photos.some((p) => p.status === 'uploading'),
  }
}

